import AbstractScenarioPlayer from "./AbstractScenarioPlayer";
import StoryFrameworkPlayerEvent from "../../../../lib/html5-software-library/src/ranj/eng/story/framework/player/StoryFrameworkPlayerEvent";
import GameEvent from "../../core/application/GameEvent";
import GoogleAnalyticsEvent from "../../analytics/GoogleAnalyticsEvent";
import ContentTypes from "../../../../lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import TrackString from "../../analytics/TrackString";
import ScenarioPlayerEvent from "./ScenarioPlayerEvent";
import StoryFrameworkPlayerFactory from "../../../../lib/html5-software-library/src/ranj/eng/story/framework/player/StoryFrameworkPlayerFactory";
import KeyDecisionsConfig from "../../app/key-decisions/model/KeyDecisionsConfig";
import KeyDecisionContentEvent from "../../app/key-decisions/event/KeyDecisionContentEvent";
import InternalStakeholdersModel from "../../app/internal-stakeholders/model/InternalStakeholdersModel";
import StoryConventions from "../../app/story-conventions/StoryConventions";
import GameClock from "../../../../lib/html5-software-library/src/ranj/eng/story/framework/clock/GameClock";

/**
 * NGTScenarioPlayer.js
 * 
 * Created on 26-sep-2013, 13:35:14
 *
 * @author Ivo Swartjes for Ranj
 **/
export default class NGTScenarioPlayer extends AbstractScenarioPlayer {
    /**
     * @constructor
     * @param {*} dispatcher 
     * @param {*} variableManager 
     * @param {GameClock} gameClock 
     * @param {KeyDecisionsConfig} keyDecisionsConfig 
     * @param {InternalStakeholdersModel} internalStakeholdersModel 
     */
    constructor(dispatcher, variableManager, gameClock, keyDecisionsConfig, internalStakeholdersModel) {
        super();
        this.eventDispatcher = dispatcher;

        this._variableManager = variableManager;
        this._gameClock = gameClock;
        this._keyDecisionsConfig = keyDecisionsConfig;
        this._internalStakeholdersModel = internalStakeholdersModel;

        this._storyEvaluationTimer;

        this._scenario;
        this._contentNodeMap = {};

        this._handlePauseGameBound = this._handlePauseGame.bind(this);
        this._handleResumeGameBound = this._handleResumeGame.bind(this);
    };

    init(scenario) {
        this._scenario = scenario;
        this._storyPlayer = StoryFrameworkPlayerFactory.createStoryFrameworkPlayer(this._scenario.story, this._gameClock, this._variableManager.solve.bind(this._variableManager));
        // TODO: text replacement.
        // TODO: use story memento if provided (save game functionality)
        this.contentNodeMap = {};
    };

    getScenario() {
        return this._scenario;
    };

    getStoryPlayer() {
        return this._storyPlayer;
    };

    play() {
        this.addEventListeners();

        this._storyEvaluationTimer = setInterval(this.handleStoryEvaluationTimer.bind(this), 100);
        this._gameClock.start();
    };

    setPaused(value) {
        if (value === true) {
            clearInterval(this._storyEvaluationTimer);
            this._gameClock.pause();
        } else {
            this._gameClock.start();
        }
    };

    stop() {
        if (this._storyPlayer === null)
            return;

        clearInterval(this._storyEvaluationTimer);
        this._gameClock.stop();

        this.removeEventListeners();
    };

    /**
     * 
     * @returns {GameClock}
     */
    getClock() {
        return this._gameClock;
    }

    contentCompleted(content) {
        console.info("NGTScenarioPlayer: Content completed");

        // Key decision handling.
        if (this._keyDecisionsConfig.contentIsKeyDecision(content)) {
            const keyDecisionContentEvent = new KeyDecisionContentEvent(KeyDecisionContentEvent.KEY_DECISION_CONTENT_COMPLETED, content, this);
            this.eventDispatcher.dispatch(keyDecisionContentEvent.type, { data: keyDecisionContentEvent });
        }

        var node = this._contentNodeMap[content.getLabel()];
        if (node) {
            this._storyPlayer.complete(node);
            delete this._contentNodeMap[content.getLabel()];
        }

        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.CONTENT_COMPLETED, content, this);
        this._storyPlayer.evaluate();
    };

    // ------------------------------------------------------------
    // Protected functions
    // ------------------------------------------------------------
    dispatchScenarioPlayerEvent(type, content, contentPlayer) {
        this.eventDispatcher.dispatch(type, {
            data:
            {
                content: content,
                contentPlayer: contentPlayer
            }
        });
    };

    addEventListeners() {
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_NODE_AVAILABLE, this.handleStoryNodeAvailable, this);
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_NODE_UNAVAILABLE, this.handleStoryNodeUnavailable, this);
        this._storyPlayer.addEventListener(StoryFrameworkPlayerEvent.STORY_END, this.handleStoryEnd, this);

        // TODO: listen to pause / resume game events.
        this.eventDispatcher.addEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this.eventDispatcher.addEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);
    };

    removeEventListeners() {
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_NODE_AVAILABLE, this.handleStoryNodeAvailable, this);
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_NODE_UNAVAILABLE, this.handleStoryNodeUnavailable, this);
        this._storyPlayer.removeEventListener(StoryFrameworkPlayerEvent.STORY_END, this.handleStoryEnd, this);

        this.eventDispatcher.removeEventListener(GameEvent.PAUSE_GAME, this._handlePauseGameBound);
        this.eventDispatcher.removeEventListener(GameEvent.RESUME_GAME, this._handleResumeGameBound);
    };

    handleStoryEvaluationTimer() {
        this._storyPlayer.evaluate();

        // Send out a game time tick for timelines etc.
        this.eventDispatcher.dispatch(GameEvent.TIME_CHANGE, { data: { time: this._gameClock.getCurrentTime() } });
    };

    handleStoryNodeAvailable(event) {
        var content = event.data.storyNode.getData();
        this._contentNodeMap[content.getLabel()] = event.data.storyNode;

        // Key decision handling.
        // NOTE: Key decision content might be handled differently as it can only be accessed from the key decisions menu.
        const isKeyDecision = this._keyDecisionsConfig.contentIsKeyDecision(content);
        if (isKeyDecision) {
            const keyDecisionAvailabilityEvent = new KeyDecisionContentEvent(KeyDecisionContentEvent.KEY_DECISION_CONTENT_AVAILABLE, content, this);
            this.eventDispatcher.dispatch(keyDecisionAvailabilityEvent.type, { data: keyDecisionAvailabilityEvent });
        }

        // Decide what the availability of this story node means for the game
        switch (content.getType()) {
            case ContentTypes.TYPE_DIALOG:
                if (isKeyDecision) {
                    // If this dialog is a key decision we don't want to add it to the pull content manager or directly push it to the player.
                    // Instead we handle this separately through the key decision model and menu.
                    break;
                }

                if (StoryConventions.isInternalStakeholderId(content.getNpcID(), this._internalStakeholdersModel.internalStakeholderIds)) {
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.INTERNAL_STAKEHOLDER_CONTENT_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.INTERNAL_STAKEHOLDER_CONTENT_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                    break;
                }

                if (content.getPlayerInitiated()) {
                    // Pull dialog (needs to be initiated by player)
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PULL_DIALOG_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_DIALOG_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                } else {
                    // Push dialog (typically played immediately)
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_DIALOG_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                }
                break;

            case ContentTypes.TYPE_CHANGE_ENTITY_VISIBILITY:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.CHANGE_ENTITY_VISIBILITY_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.CHANGE_ENTITY_VISIBILITY_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_NARRATIVE_EVENT:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.NARRATIVE_EVENT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.NARRATIVE_EVENT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_QUIZ:
                if (isKeyDecision) {
                    // If this mc decision is a key decision we push it directly to the player.
                    // Instead we handle this separately through the key decision model and menu.
                    break;
                }

                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PUSH_DECISION_DIALOG_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_DECISION_DIALOG_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_DECISION:
                if (isKeyDecision) {
                    // If this mc decision is a key decision we push it directly to the player.
                    // Instead we handle this separately through the key decision model and menu.
                    break;
                }

                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PUSH_DECISION_DIALOG_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_DECISION_DIALOG_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_CHAT:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.CHAT_DIALOG_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.CHAT_DIALOG_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_MEETING_NOTES:
                if (StoryConventions.isInternalStakeholderId(content.getNotesTitle(), this._internalStakeholdersModel.internalStakeholderIds)) {
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.INTERNAL_STAKEHOLDER_CONTENT_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.INTERNAL_STAKEHOLDER_CONTENT_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                    break;
                }

                if (content.getNotesTitle() === StoryConventions.TEA_CORNER_TITLE) {
                    // Coffee corner content is pull content
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_AVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_AVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                    break;
                }

                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PUSH_CONVERSATION_STATEMENT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PUSH_CONVERSATION_STATEMENT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_MOVIE:
                console.info("NGTScenarioPlayer: Movie available: " + event.data.storyNode.id);
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.MOVIE_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.MOVIE_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_SET_VARIABLE:
                this.handleSetVariableContent(content);
                break;

            // Workspace content    
            case ContentTypes.TYPE_FILE:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.FILE_CONTENT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.FILE_CONTENT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // Smartphone content
            case ContentTypes.TYPE_SMS:
            case ContentTypes.TYPE_VOICEMAIL:
            case ContentTypes.TYPE_PHONECALL:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PDA_CONTENT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PDA_CONTENT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;
            case ContentTypes.TYPE_EMAIL:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.EMAIL_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.EMAIL_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;
            case ContentTypes.TYPE_EMAIL_DRAFT:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.DRAFT_EMAIL_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.DRAFT_EMAIL_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // TODO: key moment

            // TODO: NPC
            case ContentTypes.TYPE_NPC:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.NPC_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.NPC_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            // TODO: Narrative event

            case ContentTypes.TYPE_TEXT:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.TEXT_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.TEXT_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_UNLOCK_LOCATION:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.UNLOCK_LOCATION_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.UNLOCK_LOCATION_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            case ContentTypes.TYPE_MAP_MUTATION:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.MAP_MUTATION_AVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.MAP_MUTATION_AVAILABLE, content, this);
                } else {
                    this.ignoreContent(content);
                }
                break;

            default:
                this.ignoreContent(content);
                break;
        }
    };

    handleStoryNodeUnavailable(event) {
        var content = event.data.storyNode.getData();
        this._contentNodeMap[content.getLabel()] = event.data.storyNode;

        // Key decision handling.
        if (this._keyDecisionsConfig.contentIsKeyDecision(content)) {
            const keyDecisionAvailabilityEvent = new KeyDecisionContentEvent(KeyDecisionContentEvent.KEY_DECISION_CONTENT_UNAVAILABLE, content, this);
            this.eventDispatcher.dispatch(keyDecisionAvailabilityEvent.type, { data: keyDecisionAvailabilityEvent });
        }

        switch (content.getType()) {
            case ContentTypes.TYPE_DIALOG:
                if (content.getPlayerInitiated()) {
                    // Pull dialog: let the game know.
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_DIALOG_UNAVAILABLE, content, this);
                }
                break;
            case ContentTypes.TYPE_FILE:
                if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.FILE_CONTENT_UNAVAILABLE)) {
                    this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.FILE_CONTENT_UNAVAILABLE, content, this);
                }
                break;
            case ContentTypes.TYPE_MEETING_NOTES:
                if (content.getNotesTitle() === StoryConventions.TEA_CORNER_TITLE) {
                    if (this.eventDispatcher.hasEventListener(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_UNAVAILABLE)) {
                        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.PULL_CONVERSATION_STATEMENT_UNAVAILABLE, content, this);
                    } else {
                        this.ignoreContent(content);
                    }
                }
                break;
        }
    };

    handleSetVariableContent(content) {
        var variableMutations = content.getVariableMutations();
        for (var i = 0; i < variableMutations.length; i++) {
            var mut = variableMutations[i];
            this._variableManager.setVariable(mut.variableName, mut.value, mut.mutationType);
        }
        this.contentCompleted(content);
    };

    handleSetNumberContent(content) {
        this._variableManager.setNumber(content.name, content.value, content.operator);
        this.contentCompleted(content);
    };

    handleStoryEnd(event) {
        // TODO: dispatch scenario end.
        this.dispatchScenarioPlayerEvent(ScenarioPlayerEvent.SCENARIO_END);
    };

    ignoreContent(content) {
        console.warn("WARNING: content type " + content.getType() + " not handled by game!");
        this.contentCompleted(content);
    };

    _handlePauseGame() {
        if (ranj.DEBUG) {
            console.log("[NGTScenarioPlayer] Pause scenario player.");
        }
        this._gameClock.pause();
    }

    _handleResumeGame() {
        if (ranj.DEBUG) {
            console.log("[NGTScenarioPlayer] Resume scenario player.");
        }
        this._gameClock.start();
    }
}
